// 1. React
import { memo, useCallback, useEffect, useState } from "react";

// 2. Helpers
import { useTranslate } from "helpers";

// 3. Types
import { VehicleOfferSaleEditProps } from "./types";
import { OfferSaleStatusHistory } from "services/api/fleet/vehicles/offers/types";
import { getOfferSaleIsAvailableAPI, toggleOfferSaleStatusHistoryAPI } from "services/api/fleet/vehicles/offers/api";
import { OfferSaleStatusId } from "services/api/fleet/vehicles/offers/consts";

// 4. Hooks
import { useAvailability } from "services/api/hooks/useAvailability";

// 5. UI components
import { Modal, ModalIntro, SuccessOverlay } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import { Tabs } from "ui/components/Tabs";
import { Loader } from "ui/components/Loader";
import invoiceAnimation from "ui/theme/animations/invoice.json";
import successAnimation from "ui/theme/animations/success.json";

// 5. Local components
import VehicleOfferSaleInfo from "./components/VehicleOfferSaleInfo/VehicleOfferSaleInfo";
import OfferStatusToolbar from "./components/VehicleOfferSaleInfo/components/OfferHeader/OfferStatusToolbar";

// 6. Consts
import {
    FLEET_VEHICLE_OFFER_SALE_CREATE_PAGE_TITLE,
    FLEET_VEHICLE_OFFER_SALE_EDIT_PAGE_TITLE,
} from "ui/components/PageTitle/consts";


const VehicleOfferSaleEditCard: React.FC<VehicleOfferSaleEditProps> = (props) => {
    const { show, vehicleId, offerId, onSuccess, onClose, onCreateContract } = props;

    const { t } = useTranslate();

    const [statusHistory, setStatusHistory] = useState<OfferSaleStatusHistory[]>([]);
    const [currentOfferId, setCurrentOfferId] = useState<string | undefined>(offerId);
    const [currentStatusId, setCurrentStatusId] = useState<string | null>(null);

    const [showSuccessOverlay, setShowSuccessOverlay] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    // Intro animacija — prikazuje se uvijek (i za nove i za edit), gasi se tek kad se pojavi PDF
    const [showIntro, setShowIntro] = useState<boolean>(true);
    // PDF pregled — podignuto iz OfferSaleInfo da parent može dohvatiti PDF pri otvaranju
    const [pdfUrl, setPdfUrl] = useState<string>("");
    const [showPdf, setShowPdf] = useState<boolean>(false);

    // Kreiranje vs ažuriranje ponude
    const isNewOffer = !offerId;

    // Provjera preduvjeta (postavke, podaci o tvrtki i stanje vozila) — samo za nove ponude
    const checkAvailability = useCallback(
        () => getOfferSaleIsAvailableAPI(vehicleId),
        [vehicleId]
    );

    const { isAvailable, renderUnavailable } = useAvailability({
        apiFn: checkAvailability,
        enabled: show && isNewOffer,
    });

    // Forma se prikazuje kad nije loading i preduvjeti su ispunjeni (ili je edit mode)
    const showForm = !isLoading && (isAvailable || !isNewOffer);

    // Reset state kad se modal otvori ili zatvori
    useEffect(() => {
        setStatusHistory([]);
        setCurrentOfferId(offerId);
        setCurrentStatusId(null);
        setShowSuccessOverlay(false);
        setShowIntro(true);
        setPdfUrl("");
        setShowPdf(false);
        setIsLoading(!!offerId);
    }, [show, offerId]);

    // Zatvaranje success overlaya nakon kreiranja ponude
    const handleSuccessDismiss = () => {
        setShowSuccessOverlay(false);
    };

    // Sinkronizacija statusa iz child komponente (VehicleOfferSaleInfo)
    const handleStatusHistoryChange = (history: OfferSaleStatusHistory[], offerIdFromChild?: string, statusId?: string | null) => {
        setStatusHistory(history);
        if (offerIdFromChild) setCurrentOfferId(offerIdFromChild);
        if (statusId !== undefined) setCurrentStatusId(statusId ?? null);
    };

    // Promjena statusa ponude (npr. Poslano, Prihvaćeno, Plaćeno)
    const handleStatusToggle = async (statusId: string, checked: boolean) => {
        if (!currentOfferId) return;

        try {
            const result = await toggleOfferSaleStatusHistoryAPI(currentOfferId, statusId, checked);
            setStatusHistory(result.statusHistory ?? []);
            setCurrentStatusId(result.status?.id ?? null);
        } catch {
            // error handled by snackbar
        }
    };

    return (
        <>
            <Modal
                show={show}
                onHide={onClose}
                position="side"
                size="medium"
                intro={showIntro ? {
                    content: (
                        <ModalIntro
                            animation={invoiceAnimation}
                            title={t("admin.fleet.vehicle.offer.sale.intro.title")}
                            description={t("admin.fleet.vehicle.offer.sale.intro.description")}
                            show={showIntro}
                        />
                    ),
                } : undefined}
            >
                <ModalHeader
                    icon="/media/images/add-icon.svg"
                    title={
                        offerId
                            ? FLEET_VEHICLE_OFFER_SALE_EDIT_PAGE_TITLE.text
                            : FLEET_VEHICLE_OFFER_SALE_CREATE_PAGE_TITLE.text
                    }
                    onHide={onClose}
                />
                <ModalBody>
                    {isLoading && (
                        <div className="relative h-[calc(100vh-80px)]">
                            <Loader isLoading={true}>{null}</Loader>
                        </div>
                    )}
                    {renderUnavailable({ title: t("admin.fleet.vehicle.offer.sale.unavailable.title"), onClose })}
                    <form noValidate className={showForm ? "" : "hidden"}>
                        <div className="px-4 mt-6">
                            <OfferStatusToolbar
                                statusHistory={statusHistory}
                                onToggle={handleStatusToggle}
                                disabled={currentStatusId === OfferSaleStatusId.Cancelled}
                            />
                        </div>
                        <div className="flex mt-4 mb-6 px-4 min-h-[70vh] xl:min-h-[1000px]">
                            <Tabs
                                tabs={[
                                    {
                                        label: t("admin.fleet.vehicle.offer.sale.add.tab.info"),
                                        content: <div className="px-4 pt-3 pb-2 border-x border-b border-slate-200 rounded-b-md">
                                            <VehicleOfferSaleInfo
                                                vehicleId={vehicleId}
                                                offerId={offerId}
                                                onStatusHistoryChange={handleStatusHistoryChange}
                                                currentStatusId={currentStatusId}
                                                onFinish={() => setShowSuccessOverlay(true)}
                                                onCreateContract={onCreateContract}
                                                setIsLoading={setIsLoading}
                                                onClose={onClose}
                                                hasIntro={showIntro}
                                                pdfUrl={pdfUrl}
                                                setPdfUrl={setPdfUrl}
                                                showPdf={showPdf}
                                                setShowPdf={setShowPdf}
                                            />
                                        </div>,
                                    },
                                ]}
                            />
                        </div>
                    </form>
                </ModalBody>
            </Modal>
            <SuccessOverlay
                show={showSuccessOverlay}
                animation={successAnimation}
                title={t("admin.fleet.vehicle.offer.sale.create.success.title")}
                description={t("admin.fleet.vehicle.offer.sale.create.success.description")}
                onDismiss={handleSuccessDismiss}
            />
        </>
    );
};
export default memo(VehicleOfferSaleEditCard);
